"use client";

import { useCallback, useState } from "react";
import { CheckIcon, CopyIcon } from "../ui/atoms/icons";

/**
 * De quoi faire venir quelqu'un : le code, en grand parce qu'il se dicte, et le
 * lien, en un clic parce qu'il s'envoie.
 *
 * # Un seul dessin, pour le récital et pour la séance à plusieurs
 *
 * « La salle » d'un récital montrait le lien dans un champ et le code dans un
 * encadré, la séance à plusieurs un code nu et un lien à cliquer : deux
 * réponses à la même question — « comment on entre ? » —, et c'est la seconde
 * que l'utilisateur a gardée (2026-09-23). Le lien entier dans un champ ne se
 * lit pas plus qu'il ne se dicte ; c'est le code qu'on lit, et le lien qu'on
 * copie.
 *
 * Le code n'est là que si l'API l'a rendu à qui regarde — l'auteur, et tout le
 * monde pour un récital public. Sans lui, le lien reste, et mène au même
 * endroit.
 */
export function ShareInvite({
  code,
  shareUrl,
  copyShareUrl,
}: {
  code: string | null;
  shareUrl: string | null;
  /** Recopie le lien ; faux si le navigateur refuse le presse-papier. */
  copyShareUrl: () => Promise<boolean>;
}) {
  const [codeCopied, setCodeCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const copyCode = useCallback(async () => {
    if (code === null) {
      return;
    }

    try {
      await navigator.clipboard.writeText(code);
      setCodeCopied(true);
      window.setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      // Presse-papier refusé : le code reste sélectionnable à l'écran.
    }
  }, [code]);

  const copyLink = useCallback(async () => {
    const ok = await copyShareUrl();

    setLinkCopied(ok);

    if (ok) {
      window.setTimeout(() => setLinkCopied(false), 2000);
    }
  }, [copyShareUrl]);

  if (code === null && shareUrl === null) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-2">
      {code !== null && (
        <div className="flex items-center gap-2">
          {/* En mono, espacé et sans transformation de casse : les majuscules
              comptent, et c'est au moment de le dicter qu'il doit se lire. */}
          <span
            aria-label={`Code : ${code}`}
            className="min-w-0 flex-1 select-all font-mono text-[22px] leading-8 tracking-[0.22em] text-text-primary"
          >
            {code}
          </span>

          <button
            type="button"
            onClick={() => void copyCode()}
            title="Copier le code"
            aria-label="Copier le code"
            className="grid size-8 shrink-0 cursor-pointer place-items-center rounded-md text-text-muted transition-colors duration-150 hover:bg-white/[0.06] hover:text-text-primary"
          >
            {codeCopied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
          </button>
        </div>
      )}

      {shareUrl !== null && (
        <button
          type="button"
          onClick={() => void copyLink()}
          className="inline-flex w-fit cursor-pointer items-center gap-1.5 rounded text-[12px] text-accent-bright transition-colors duration-150 hover:text-text-primary"
        >
          {linkCopied ? <CheckIcon size={13} /> : <CopyIcon size={13} />}
          {linkCopied ? "Lien copié" : code !== null ? "Ou copier le lien" : "Copier le lien"}
        </button>
      )}
    </div>
  );
}
